/**
 * Routing bridge: data routes render nothing and only register/activate a tab.
 * Content lives in WorkspaceHost.
 */
import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import { useAppWorkspaceStore, type TabKind } from './appWorkspaceStore'
import { tabPathFromLocation } from './isAppDataPath'
import { getLastServicePath } from './lastServicePath'
import { clearUserClosed, consumeUserClosed } from './closeWorkspaceTab'
import { resolveLiveTabPath } from './tabPathAliases'

export interface WorkspaceRouteProps {
  kind: TabKind
}

export const WorkspaceRoute: React.FC<WorkspaceRouteProps> = ({ kind }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const params = useParams<{ objectName?: string; recordId?: string; reportId?: string }>()
  const [limitOpen, setLimitOpen] = useState(false)

  const path = tabPathFromLocation(location.pathname, location.search)

  useEffect(() => {
    const provisionalTitle =
      kind === 'print'
        ? 'Print'
        : kind === 'mail'
          ? 'Mail'
          : kind === 'report'
          ? params.reportId || 'Report'
          : params.recordId && params.recordId !== 'new'
            ? `${params.objectName || 'Record'}`
            : params.objectName || 'Untitled'

    const ensureTab = () => {
      clearUserClosed(path)
      const store = useAppWorkspaceStore.getState()
      const wasEmpty = store.tabs.length === 0

      // Records start unresolved so a modal schema never flashes full-area (§4.6).
      // Lists/reports open as tabs immediately — list openMode is deferred, and
      // waiting on resolveFormSchema left every list blank when the fetch failed.
      const result = store.openTab({
        path,
        kind,
        title: provisionalTitle,
        isDirty: false,
        presentation: kind === 'record' ? 'unresolved' : 'tab',
      })

      if (!result.ok) {
        setLimitOpen(true)
        navigateRef.current(-1)
        return
      }

      if (wasEmpty) {
        store.setReturnPath(getLastServicePath())
      }
      store.setActive(path)
    }

    ensureTab()

    // If session hydrate/reset wipes this tab while we stay on the route, reopen.
    // Do NOT put tabPresent in React deps — openTab/setActive would loop (#185).
    return useAppWorkspaceStore.subscribe((state, prev) => {
      const had = prev.tabs.some((t) => t.path === path)
      const has = state.tabs.some((t) => t.path === path)
      // A user close removes the tab while this route is still mounted —
      // resurrecting it would leave a title in the strip with no content.
      if (!had || has || consumeUserClosed(path)) return
      // replacePath renamed /app/X/new → /app/X/<id> while this route is still
      // on /new: the old path vanishes from tabs, but it is NOT a wipe — the
      // same tab lives at the alias. Resurrecting forks a zombie /new; the
      // first ✕ then only closes /id and a second press is needed for the
      // leftover (also breaks the next Create — navigates to the zombie).
      const live = resolveLiveTabPath(path)
      if (live !== path && state.tabs.some((t) => t.path === live)) return
      ensureTab()
    })
  }, [path, kind, params.objectName, params.recordId, params.reportId])

  return (
    <Snackbar
      open={limitOpen}
      autoHideDuration={4000}
      onClose={() => setLimitOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="warning" onClose={() => setLimitOpen(false)} variant="filled">
        Close some tabs before opening more.
      </Alert>
    </Snackbar>
  )
}
